/**
 * One ongoing save (§21: the save model is open; this is the least of it).
 *
 * The world is plain data, so a save is the state serialized whole into
 * IndexedDB, plus the sheet's last lines. Written on a short debounce after
 * every reducer commit, read once at load. No slots. `/sim` never sees
 * this: serialization belongs to the caller (§19).
 */

import type { Seedable, WorldState } from '@sim/index.ts';

const DB = 'crime-rpg';
const STORE = 'saves';
const KEY = 'current';
export const SAVE_FORMAT = 1;

export interface Save {
  readonly format: number;
  readonly simVersion: number;
  readonly contentVersion: number;
  readonly seed: Seedable;
  readonly state: WorldState;
  readonly lines: readonly { readonly kind: string; readonly text: string }[];
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no IndexedDB'));
      return;
    }
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB failed to open'));
  });
}

function run<T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = op(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        tx.oncomplete = () => db.close();
      }),
  );
}

export async function loadSave(): Promise<Save | null> {
  try {
    const raw = await run<unknown>('readonly', (s) => s.get(KEY));
    if (typeof raw !== 'object' || raw === null) return null;
    const save = raw as Save;
    if (save.format !== SAVE_FORMAT) return null;
    return save;
  } catch {
    return null;
  }
}

export async function writeSave(save: Save): Promise<void> {
  try {
    await run('readwrite', (s) => s.put(save, KEY));
  } catch {
    // Storage blocked or full. The run continues in memory; nothing else to do.
  }
}

export async function clearSave(): Promise<void> {
  try {
    await run('readwrite', (s) => s.delete(KEY));
  } catch {
    // Same.
  }
}

/** Save at most once per `delayMs`, always with the newest value. */
export function saver(delayMs = 300): (save: Save) => void {
  let pending: Save | null = null;
  let timer: number | null = null;
  return (save) => {
    pending = save;
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      if (pending) void writeSave(pending);
      pending = null;
    }, delayMs);
  };
}
